import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import blogImg from "../assests/images/blog-bg.jpg";
import axios from "axios";
import { Helmet } from "react-helmet-async";

import {
  FaArrowRight,
  FaCalendarAlt,
  FaClock,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);

      try {
        const res = await axios.get(
          "https://marinadubai.com/api/blogs"
        );

        const data = res.data;

        setBlogs(data);

        const uniqueCategories = [
          ...new Map(
            data
              .filter((b) => b.category)
              .map((b) => [b.category.id, b.category])
          ).values(),
        ];

        setCategories(uniqueCategories);
      } catch (err) {
        console.error("Failed to fetch blogs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch = blog.title
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchesCategory = selectedCategory
      ? blog.category?.id === Number(selectedCategory)
      : true;

    return matchesSearch && matchesCategory;
  });

  const clearFilters = () => {
    setSearch("");
    setSelectedCategory("");
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <>
      {/* =========================================================
          SEO
      ========================================================= */}
      <Helmet>
        <title>
          Dubai Travel Blog & Hotel Guides | Dubai Marina
        </title>

        <meta
          name="description"
          content="Explore Dubai travel guides, hotel recommendations, destination tips and travel inspiration to help you find the perfect Dubai stay."
        />

        <link
          rel="canonical"
          href="https://marinadubai.com/blogs"
        />

        <meta
          property="og:title"
          content="Dubai Travel Blog & Hotel Guides | Dubai Marina"
        />

        <meta
          property="og:description"
          content="Explore Dubai travel guides, hotel recommendations, destination tips and travel inspiration to help you find the perfect Dubai stay."
        />

        <meta
          property="og:url"
          content="https://marinadubai.com/blogs"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Dubai Travel Blog & Hotel Guides | Dubai Marina"
        />

        <meta
          name="twitter:description"
          content="Explore Dubai travel guides, hotel recommendations, destination tips and travel inspiration to help you find the perfect Dubai stay."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>


      <div className="w-full overflow-hidden bg-[#F7F5EF]">

        {/* =====================================================
            HERO
        ====================================================== */}

        <Hero
          title="Travel Stories"
          subtitle="Discover Dubai through thoughtful guides, hotel tips, destination ideas and travel inspiration."
          backgroundImage={blogImg}
        />


        {/* =====================================================
            EDITORIAL SECTION
        ====================================================== */}

        <section className="relative overflow-hidden bg-[#F7F5EF]">

          {/* Background details */}

          <div
            className="
              pointer-events-none
              absolute
              -left-40
              top-20
              h-96
              w-96
              rounded-full
              bg-[#E7DED0]/50
              blur-3xl
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              -right-40
              top-[500px]
              h-96
              w-96
              rounded-full
              bg-[#D5A33C]/5
              blur-3xl
            "
          />

          <div
            className="
              relative
              mx-auto
              max-w-[1440px]
              px-5
              sm:px-8
              lg:px-12
              xl:px-16
            "
          >

            {/* =================================================
                INTRO
            ================================================= */}

            <div
              className="
                flex
                flex-col
                gap-7
                pb-10
                pt-14

                sm:pb-12
                sm:pt-16

                lg:flex-row
                lg:items-end
                lg:justify-between
              "
            >

              <div className="max-w-[700px]">

                {/* Eyebrow */}

                <div
                  className="
                    mb-4
                    flex
                    items-center
                    gap-3
                  "
                >
                  <span
                    className="
                      h-[1px]
                      w-9
                      bg-[#D5A33C]
                    "
                  />

                  <span
                    className="
                      text-[10px]
                      font-semibold
                      uppercase
                      tracking-[0.22em]
                      text-[#0B5552]
                    "
                  >
                    Dubai Hotel Rates
                  </span>
                </div>


                {/* Heading */}

                <h1
                  className="
                    font-serif
                    text-[32px]
                    font-medium
                    leading-[1.08]
                    tracking-[-0.025em]
                    text-[#173F3E]

                    sm:text-[40px]

                    lg:text-[50px]
                  "
                >
                  Stories for your

                  <span
                    className="
                      italic
                      text-[#B78932]
                    "
                  >
                    {" "}next Dubai journey.
                  </span>
                </h1>


                <p
                  className="
                    mt-5
                    max-w-[620px]
                    text-[12px]
                    leading-6
                    text-[#687572]

                    sm:text-[13px]
                    sm:leading-7
                  "
                >
                  Explore Dubai travel guides, hotel recommendations,
                  destination ideas and useful tips designed to help you
                  discover the city and choose the right stay with confidence.
                </p>

              </div>


              {/* Article counter */}

              {!loading && blogs.length > 0 && (
                <div
                  className="
                    flex
                    shrink-0
                    items-center
                    gap-4
                    rounded-[18px]
                    border
                    border-[#DED8CC]
                    bg-[#FBFAF6]
                    px-5
                    py-4
                    shadow-[0_10px_30px_rgba(15,45,44,0.05)]
                  "
                >

                  <div
                    className="
                      flex
                      h-11
                      w-11
                      items-center
                      justify-center
                      rounded-full
                      border
                      border-[#D5A33C]/40
                      bg-[#F3EBDD]
                      text-[#0B5552]
                    "
                  >
                    <FaClock className="text-sm" />
                  </div>

                  <div>
                    <p
                      className="
                        font-serif
                        text-[25px]
                        font-medium
                        leading-none
                        text-[#173F3E]
                      "
                    >
                      {blogs.length}
                    </p>

                    <p
                      className="
                        mt-1
                        text-[10px]
                        font-medium
                        uppercase
                        tracking-[0.12em]
                        text-[#687572]
                      "
                    >
                      Travel stories
                    </p>
                  </div>

                </div>
              )}

            </div>


            {/* =================================================
                SEARCH + CATEGORIES
            ================================================= */}

            <div className="pb-10">

              <div
                className="
                  overflow-hidden
                  rounded-[20px]
                  border
                  border-[#DED8CC]
                  bg-[#FBFAF6]
                  p-3
                  shadow-[0_12px_35px_rgba(15,45,44,0.05)]

                  sm:p-4
                "
              >

                <div
                  className="
                    flex
                    flex-col
                    gap-4

                    lg:flex-row
                    lg:items-center
                  "
                >

                  {/* Search */}

                  <div className="relative flex-1">

                    <FaSearch
                      className="
                        absolute
                        left-4
                        top-1/2
                        -translate-y-1/2
                        text-[#8A9490]
                      "
                    />

                    <input
                      type="text"
                      value={search}
                      onChange={(e) =>
                        setSearch(e.target.value)
                      }
                      placeholder="Search travel stories..."
                      className="
                        w-full
                        rounded-xl
                        border
                        border-[#DED8CC]
                        bg-[#F7F5EF]
                        py-3.5
                        pl-11
                        pr-11
                        text-sm
                        text-[#173F3E]
                        outline-none
                        transition
                        placeholder:text-[#9A9F9C]

                        focus:border-[#0B5552]
                        focus:ring-4
                        focus:ring-[#0B5552]/10
                      "
                    />

                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        className="
                          absolute
                          right-3
                          top-1/2
                          flex
                          h-7
                          w-7
                          -translate-y-1/2
                          items-center
                          justify-center
                          rounded-full
                          bg-[#E7DED0]/60
                          text-[#687572]
                          transition

                          hover:bg-[#0B5552]
                          hover:text-white
                        "
                      >
                        <FaTimes className="text-xs" />
                      </button>
                    )}

                  </div>


                  {/* Categories */}

                  <div
                    className="
                      flex
                      gap-2
                      overflow-x-auto
                      pb-1

                      lg:max-w-[58%]
                      lg:pb-0
                    "
                  >

                    <button
                      type="button"
                      onClick={() =>
                        setSelectedCategory("")
                      }
                      className={`
                        shrink-0
                        rounded-full
                        px-4
                        py-2.5
                        text-[10px]
                        font-semibold
                        uppercase
                        tracking-[0.08em]
                        transition-all
                        duration-300

                        ${selectedCategory === ""
                          ? "bg-[#0B5552] text-white shadow-md"
                          : "bg-[#EFECE5] text-[#687572] hover:bg-[#E7DED0] hover:text-[#0B5552]"
                        }
                      `}
                    >
                      All Stories
                    </button>


                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() =>
                          setSelectedCategory(
                            String(cat.id)
                          )
                        }
                        className={`
                          shrink-0
                          rounded-full
                          px-4
                          py-2.5
                          text-[10px]
                          font-semibold
                          uppercase
                          tracking-[0.08em]
                          transition-all
                          duration-300

                          ${selectedCategory ===
                            String(cat.id)
                            ? "bg-[#0B5552] text-white shadow-md"
                            : "bg-[#EFECE5] text-[#687572] hover:bg-[#E7DED0] hover:text-[#0B5552]"
                          }
                        `}
                      >
                        {cat.name}
                      </button>
                    ))}

                  </div>

                </div>

              </div>

            </div>


            {/* =================================================
                CONTENT
            ================================================= */}

            {loading ? (

              /* =================================================
                  LOADING
              ================================================= */

              <div
                className="
                  grid
                  grid-cols-1
                  gap-6
                  pb-20

                  md:grid-cols-2
                  lg:grid-cols-3
                "
              >

                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div
                    key={item}
                    className="
                      overflow-hidden
                      rounded-[24px]
                      border
                      border-[#DED8CC]
                      bg-[#FBFAF6]
                    "
                  >

                    <div
                      className="
                        h-60
                        animate-pulse
                        bg-[#E7DED0]
                      "
                    />

                    <div className="p-6">

                      <div
                        className="
                          h-3
                          w-24
                          animate-pulse
                          rounded
                          bg-[#E7DED0]
                        "
                      />

                      <div
                        className="
                          mt-4
                          h-6
                          w-full
                          animate-pulse
                          rounded
                          bg-[#E7DED0]
                        "
                      />

                      <div
                        className="
                          mt-2
                          h-6
                          w-4/5
                          animate-pulse
                          rounded
                          bg-[#E7DED0]
                        "
                      />

                      <div
                        className="
                          mt-5
                          h-4
                          w-full
                          animate-pulse
                          rounded
                          bg-[#E7DED0]
                        "
                      />

                      <div
                        className="
                          mt-2
                          h-4
                          w-2/3
                          animate-pulse
                          rounded
                          bg-[#E7DED0]
                        "
                      />

                    </div>

                  </div>
                ))}

              </div>

            ) : filteredBlogs.length > 0 ? (

              <>

                {/* =================================================
                    FEATURED STORY
                ================================================= */}

                <section className="pb-14 sm:pb-16">

                  <Link
                    to={`/blogs/${filteredBlogs[0].slug}`}
                    className="group block"
                  >

                    <div
                      className="
                        relative
                        overflow-hidden
                        rounded-[28px]
                        bg-[#073F3D]
                        shadow-[0_25px_70px_rgba(15,45,44,0.15)]
                      "
                    >

                      {/* Image */}

                      <div
                        className="
                          relative
                          h-[430px]

                          sm:h-[500px]

                          lg:h-[560px]
                        "
                      >

                        <img
                          src={
                            filteredBlogs[0]
                              .thumbnail_image
                          }
                          alt={
                            filteredBlogs[0].title
                          }
                          className="
                            absolute
                            inset-0
                            h-full
                            w-full
                            object-cover
                            transition-transform
                            duration-1000

                            group-hover:scale-105
                          "
                        />


                        {/* Elegant overlay */}

                        <div
                          className="
                            absolute
                            inset-0
                            bg-gradient-to-t
                            from-[#073F3D]
                            via-[#073F3D]/55
                            to-transparent
                          "
                        />

                      </div>


                      {/* Gold top accent */}

                      <div
                        className="
                          absolute
                          left-0
                          top-0
                          h-[3px]
                          w-full
                          bg-[#D5A33C]
                        "
                      />


                      {/* Featured badge */}

                      <div
                        className="
                          absolute
                          left-5
                          top-5

                          sm:left-7
                          sm:top-7
                        "
                      >

                        <div
                          className="
                            flex
                            items-center
                            gap-2
                            rounded-full
                            border
                            border-[#E5C56D]/40
                            bg-[#D5A33C]
                            px-4
                            py-2
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.16em]
                            text-[#173F3E]
                            shadow-lg
                          "
                        >

                          <span
                            className="
                              h-1.5
                              w-1.5
                              rounded-full
                              bg-[#173F3E]
                            "
                          />

                          Featured Story

                        </div>

                      </div>


                      {/* Content */}

                      <div
                        className="
                          absolute
                          bottom-0
                          left-0
                          w-full
                          p-5

                          sm:p-8

                          lg:p-10
                        "
                      >

                        <div
                          className="
                            max-w-4xl
                            lg:pr-20
                          "
                        >

                          <div
                            className="
                              flex
                              flex-wrap
                              items-center
                              gap-3
                            "
                          >

                            {filteredBlogs[0]
                              .category && (
                                <span
                                  className="
                                  rounded-full
                                  border
                                  border-white/20
                                  bg-white/10
                                  px-3
                                  py-1.5
                                  text-[9px]
                                  font-semibold
                                  uppercase
                                  tracking-[0.12em]
                                  text-white
                                  backdrop-blur-md
                                "
                                >
                                  {
                                    filteredBlogs[0]
                                      .category
                                      ?.name
                                  }
                                </span>
                              )}

                            <span
                              className="
                                flex
                                items-center
                                gap-2
                                text-[11px]
                                text-white/65
                              "
                            >
                              <FaCalendarAlt className="text-[#E5C56D]" />

                              {formatDate(
                                filteredBlogs[0]
                                  .created_at
                              )}
                            </span>

                          </div>


                          <h2
                            className="
                              mt-4
                              font-serif
                              text-2xl
                              font-medium
                              leading-tight
                              !text-white

                              sm:text-3xl

                              lg:text-5xl
                            "
                          >
                            {
                              filteredBlogs[0]
                                .title
                            }
                          </h2>


                          <p
                            className="
                              mt-4
                              max-w-2xl
                              text-[12px]
                              leading-6
                              text-white/70

                              sm:text-[13px]
                            "
                          >
                            {
                              filteredBlogs[0]
                                .excerpt
                            }
                          </p>


                          <div
                            className="
                              mt-6
                              inline-flex
                              items-center
                              gap-3
                              text-[11px]
                              font-semibold
                              uppercase
                              tracking-[0.12em]
                              text-[#E5C56D]
                            "
                          >

                            Read Featured Story

                            <span
                              className="
                                flex
                                h-9
                                w-9
                                items-center
                                justify-center
                                rounded-full
                                bg-[#FBFAF6]
                                text-[#0B5552]
                                transition-all
                                duration-300

                                group-hover:translate-x-1
                              "
                            >
                              <FaArrowRight className="text-xs" />
                            </span>

                          </div>

                        </div>

                      </div>

                    </div>

                  </Link>

                </section>


                {/* =================================================
                    LATEST STORIES HEADER
                ================================================= */}

                <div
                  className="
                    mb-7
                    flex
                    items-end
                    justify-between
                    gap-4
                  "
                >

                  <div>

                    <div
                      className="
                        mb-3
                        flex
                        items-center
                        gap-3
                      "
                    >

                      <span
                        className="
                          h-[1px]
                          w-8
                          bg-[#D5A33C]
                        "
                      />

                      <span
                        className="
                          text-[10px]
                          font-semibold
                          uppercase
                          tracking-[0.2em]
                          text-[#0B5552]
                        "
                      >
                        Latest Stories
                      </span>

                    </div>


                    <h2
                      className="
                        font-serif
                        text-2xl
                        font-medium
                        text-[#173F3E]

                        sm:text-3xl
                      "
                    >
                      More Travel Inspiration
                    </h2>

                  </div>


                  <span
                    className="
                      hidden
                      text-[10px]
                      font-medium
                      uppercase
                      tracking-[0.12em]
                      text-[#8A9490]

                      sm:block
                    "
                  >
                    {filteredBlogs.length} stories
                  </span>

                </div>


                {/* =================================================
                    BLOG GRID
                ================================================= */}

                <section
                  className="
                    grid
                    grid-cols-1
                    gap-6
                    pb-20

                    md:grid-cols-2
                    lg:grid-cols-3
                  "
                >

                  {filteredBlogs.slice(1).map(
                    (blog, index) => (
                      <article
                        key={blog.id}
                        className="
                          group
                          overflow-hidden
                          rounded-[24px]
                          border
                          border-[#DED8CC]
                          bg-[#FBFAF6]
                          shadow-[0_8px_30px_rgba(15,45,44,0.045)]
                          transition-all
                          duration-500

                          hover:-translate-y-2
                          hover:border-[#0B5552]/20
                          hover:shadow-[0_20px_50px_rgba(15,45,44,0.10)]
                        "
                      >

                        {/* Image */}

                        <Link
                          to={`/blogs/${blog.slug}`}
                          className="block"
                        >

                          <div
                            className="
                              relative
                              h-60
                              overflow-hidden
                            "
                          >

                            <img
                              src={
                                blog.thumbnail_image
                              }
                              alt={blog.title}
                              loading="lazy"
                              className="
                                h-full
                                w-full
                                object-cover
                                transition-transform
                                duration-700

                                group-hover:scale-110
                              "
                            />


                            {/* Image overlay */}

                            <div
                              className="
                                absolute
                                inset-0
                                bg-gradient-to-t
                                from-[#073F3D]/70
                                via-transparent
                                to-transparent
                              "
                            />


                            {/* Number */}

                            <span
                              className="
                                absolute
                                left-4
                                top-4
                                flex
                                h-9
                                min-w-9
                                items-center
                                justify-center
                                rounded-full
                                border
                                border-white/20
                                bg-[#073F3D]/75
                                px-2
                                font-serif
                                text-xs
                                italic
                                text-white
                                backdrop-blur-md
                              "
                            >
                              {String(
                                index + 2
                              ).padStart(
                                2,
                                "0"
                              )}
                            </span>


                            {/* Category */}

                            {blog.category && (
                              <span
                                className="
                                  absolute
                                  right-4
                                  top-4
                                  rounded-full
                                  border
                                  border-white/20
                                  bg-[#FBFAF6]/95
                                  px-3
                                  py-1.5
                                  text-[9px]
                                  font-bold
                                  uppercase
                                  tracking-[0.08em]
                                  text-[#0B5552]
                                  shadow-md
                                "
                              >
                                {
                                  blog.category
                                    ?.name
                                }
                              </span>
                            )}

                          </div>

                        </Link>


                        {/* Content */}

                        <div className="p-6">

                          {/* Date */}

                          <div
                            className="
                              flex
                              items-center
                              gap-2
                              text-[10px]
                              font-medium
                              uppercase
                              tracking-[0.08em]
                              text-[#8A9490]
                            "
                          >

                            <FaCalendarAlt className="text-[#B78932]" />

                            {formatDate(
                              blog.created_at
                            )}

                          </div>


                          {/* Title */}

                          <Link
                            to={`/blogs/${blog.slug}`}
                          >

                            <h3
                              className="
                                mt-3
                                line-clamp-2
                                font-serif
                                text-[20px]
                                font-medium
                                leading-snug
                                text-[#173F3E]
                                transition-colors
                                duration-300

                                group-hover:text-[#0B5552]
                              "
                            >
                              {blog.title}
                            </h3>

                          </Link>


                          {/* Excerpt */}

                          <p
                            className="
                              mt-3
                              line-clamp-3
                              min-h-[72px]
                              text-[11px]
                              leading-6
                              text-[#77827F]
                            "
                          >
                            {blog.excerpt}
                          </p>


                          {/* Bottom */}

                          <div
                            className="
                              mt-5
                              flex
                              items-center
                              justify-between
                              border-t
                              border-[#DED8CC]
                              pt-5
                            "
                          >

                            <span
                              className="
                                flex
                                items-center
                                gap-2
                                text-[10px]
                                font-medium
                                uppercase
                                tracking-[0.08em]
                                text-[#8A9490]
                              "
                            >
                              <FaClock />

                              5 min read
                            </span>


                            <Link
                              to={`/blogs/${blog.slug}`}
                              className="
                                inline-flex
                                items-center
                                gap-2
                                text-[10px]
                                font-bold
                                uppercase
                                tracking-[0.08em]
                                text-[#0B5552]
                                transition-all
                                duration-300

                                group-hover:text-[#B78932]
                              "
                            >
                              Read Story

                              <FaArrowRight
                                className="
                                  text-[9px]
                                  transition-transform
                                  duration-300

                                  group-hover:translate-x-1
                                "
                              />
                            </Link>

                          </div>

                        </div>


                        {/* Gold bottom accent */}

                        <div
                          className="
                            h-[2px]
                            w-0
                            bg-[#D5A33C]
                            transition-all
                            duration-500

                            group-hover:w-full
                          "
                        />

                      </article>
                    )
                  )}

                </section>

              </>

            ) : (

              /* =================================================
                  EMPTY STATE
              ================================================= */

              <div
                className="
                  mb-20
                  rounded-[28px]
                  border
                  border-[#DED8CC]
                  bg-[#FBFAF6]
                  px-6
                  py-20
                  text-center
                  shadow-[0_10px_30px_rgba(15,45,44,0.04)]
                "
              >

                <div
                  className="
                    mx-auto
                    flex
                    h-16
                    w-16
                    items-center
                    justify-center
                    rounded-full
                    border
                    border-[#D5A33C]/40
                    bg-[#F3EBDD]
                    text-[#0B5552]
                  "
                >
                  <FaSearch />
                </div>


                <h3
                  className="
                    mt-5
                    font-serif
                    text-2xl
                    font-medium
                    text-[#173F3E]
                  "
                >
                  No stories found
                </h3>


                <p
                  className="
                    mx-auto
                    mt-3
                    max-w-md
                    text-[11px]
                    leading-6
                    text-[#77827F]
                  "
                >
                  We couldn't find any articles matching
                  your search or selected category.
                </p>


                <button
                  type="button"
                  onClick={clearFilters}
                  className="
                    mt-6
                    inline-flex
                    items-center
                    gap-2
                    rounded-xl
                    bg-[#0B5552]
                    px-5
                    py-3
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.08em]
                    text-white
                    shadow-md
                    transition-all
                    duration-300

                    hover:-translate-y-0.5
                    hover:bg-[#073F3D]
                    hover:shadow-lg
                  "
                >
                  View All Stories

                  <FaArrowRight className="text-[9px]" />
                </button>

              </div>
            )}

          </div>

        </section>

      </div>
    </>
  );
}